<script setup>
import { t } from '../i18n';

// Sort chips, the «По секторам» switch and the ⇅ reverse button above the positions
// (docs/portfolios/SPEC.md, decision 23; docs/design/SPEC.md, decision 34). Chips are icons only.
defineProps({
  keys: {
    type: Array,
    required: true,
  },
  sortKey: {
    type: String,
    required: true,
  },
  desc: {
    type: Boolean,
    required: true,
  },
  // Positions grouped by sector inside every type
  bySector: {
    type: Boolean,
    default: false,
  },
  // Desktop: small buttons instead of phone chips
  compact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['choose', 'reverse', 'toggle-sectors']);

// Coins for the value, a growth arrow for both gains (the unit sits next to it).
const COINS = 'M5 6.5a7 2.5 0 1 0 14 0a7 2.5 0 1 0-14 0M5 6.5v5c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-5M5 11.5v5c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-5';
const GROWTH = 'M4 16.5l5-5 3 3 6.5-6.5M14.5 8h4v4';
const SECTORS = 'M4.5 4.5h6v6h-6zM13.5 4.5h6v6h-6zM4.5 13.5h6v6h-6zM13.5 13.5h6v6h-6z';
const UNIT = { yieldAbs: '₽', yieldPercent: '%' };

const COMPACT = 'inline-flex h-7 min-w-8 shrink-0 items-center justify-center gap-0.5 rounded-full px-2 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400';
// Phone: the chip of ui.js with less padding, so the four icons, the switch and ⇅ fit 390 px.
const CHIP = 'relative inline-flex h-9 min-w-11 shrink-0 items-center justify-center gap-0.5 rounded-full px-3 text-[0.875rem] font-semibold before:absolute before:inset-x-0 before:-inset-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400';

function keyClass(selected, compact) {
  if (compact) {
    return selected ? `${COMPACT} bg-slate-200 text-slate-950` : `${COMPACT} bg-slate-800 text-slate-300 hover:bg-slate-700`;
  }
  return selected ? `${CHIP} bg-slate-100 text-slate-950` : `${CHIP} bg-slate-800 text-slate-300`;
}

function roundClass(compact) {
  return compact
    ? 'flex size-7 shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400'
    : 'relative flex size-9 shrink-0 items-center justify-center rounded-full before:absolute before:-inset-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400';
}
</script>

<template>
  <div class="flex items-center gap-2">
    <div
      :class="compact ? 'flex min-w-0 flex-1 flex-wrap gap-1.5' : 'flex min-w-0 flex-1 gap-2 overflow-x-auto py-1 [scrollbar-width:none]'"
      role="group"
      :aria-label="t('portfolio.sort')"
    >
      <button
        v-for="key in keys"
        :key="key"
        type="button"
        :aria-pressed="sortKey === key"
        :aria-label="t(`portfolio.sort.${key}`)"
        :class="keyClass(sortKey === key, compact)"
        @click="emit('choose', key)"
      >
        <span
          v-if="key === 'name'"
          aria-hidden="true"
          :class="compact ? 'text-[0.6875rem]' : 'text-[0.8125rem]'"
        >{{ t('portfolio.sort.nameGlyph') }}</span>
        <template v-else>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
            :class="compact ? 'size-[15px]' : 'size-[18px]'"
            aria-hidden="true"
          >
            <path :d="key === 'value' ? COINS : GROWTH" />
          </svg>
          <span
            v-if="UNIT[key]"
            aria-hidden="true"
            :class="compact ? 'text-[0.625rem]' : 'text-xs'"
          >{{ UNIT[key] }}</span>
        </template>
      </button>
    </div>
    <span
      class="w-px shrink-0 bg-slate-700"
      :class="compact ? 'h-4' : 'h-5'"
      aria-hidden="true"
    />
    <!-- On/off, independent of the sort: the sort then works inside every sector -->
    <button
      type="button"
      :class="[roundClass(compact), bySector
        ? 'text-slate-100 ring-[1.5px] ring-inset ring-slate-100'
        : (compact ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-800 text-slate-300')]"
      :aria-pressed="bySector"
      :aria-label="t('portfolio.sort.bySector')"
      @click="emit('toggle-sectors')"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
        stroke-linejoin="round"
        :class="compact ? 'size-[15px]' : 'size-[18px]'"
        aria-hidden="true"
      >
        <path :d="SECTORS" />
      </svg>
    </button>
    <!-- Up and down arrows; the one of the current direction is bright -->
    <button
      type="button"
      :class="[roundClass(compact), compact ? 'bg-slate-800 hover:bg-slate-700' : 'bg-slate-800']"
      :aria-label="t('portfolio.sort.reverse')"
      @click="emit('reverse')"
    >
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
        :class="compact ? 'size-4' : 'size-5'"
        aria-hidden="true"
      >
        <path
          d="M6 15V4M3 7l3-3 3 3"
          :class="desc ? 'text-slate-500' : 'text-slate-100'"
        />
        <path
          d="M14 5v11M11 13l3 3 3-3"
          :class="desc ? 'text-slate-100' : 'text-slate-500'"
        />
      </svg>
    </button>
  </div>
</template>
